/**
 * /settings/quick-replies — Respostas rápidas (pedido da PoolTrack, 02/10:
 * "assim como no Blip, mensagens rápidas padronizadas"). Texto pronto que o
 * atendente insere na conversa pelo `/atalho` ou pelo botão "Respostas
 * rápidas", para revisar antes de enviar.
 *
 * Leitura para todo o staff que atende (é a mesma lista do compositor); escrita
 * só do admin — a API barra, a tela esconde os controles. Desativar tira do
 * compositor sem apagar. O editor mostra as variáveis do tenant (no vocabulário
 * dele) e uma prévia com valores de exemplo, na formatação do WhatsApp.
 */
import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import {
  QUICK_REPLY_BODY_MAX,
  QUICK_REPLY_SHORTCUT_MAX,
  QUICK_REPLY_SHORTCUT_RE,
  QUICK_REPLY_TITLE_MAX,
  fillQuickReply,
  filterQuickReplies,
  firstNameOf,
  quickReplyVariables,
  slugifyShortcut,
  unresolvedVariables,
  variableToken,
  type QuickReply,
  type QuickReplyVariable,
} from '@erp/shared';
import { useAuthUser } from '@/auth/use-auth';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge, Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { DeskRowAction } from '@/features/desks/desk-stages-section';
import { desksListQuery } from '@/features/tickets/tickets-queries';
import { WhatsappText } from '@/features/chats/whatsapp-text';
import {
  quickRepliesOptions,
  quickReplyErrorMessage,
  useDeleteQuickReply,
  useSaveQuickReply,
} from '@/features/chats/quick-replies-queries';
import { SettingsBreadcrumb, SettingsSearch } from '@/features/settings/settings-ui';
import { useTenantProfile } from '@/shared/tenant-profile';

/** As variáveis deste tenant (o identificador e o cliente vêm do vocabulário). */
function useVariables(): QuickReplyVariable[] {
  const profile = useTenantProfile();
  return useMemo(
    () => quickReplyVariables({ terms: profile.terms, resourceSource: profile.resource.source }),
    [profile]
  );
}

export function QuickRepliesPage() {
  const user = useAuthUser();
  const isAdmin = user?.role === 'admin';
  const listQ = useQuery(quickRepliesOptions());
  const desksQ = useQuery(desksListQuery());
  const save = useSaveQuickReply();
  const remove = useDeleteQuickReply();
  const [query, setQuery] = useState('');
  const [deskFilter, setDeskFilter] = useState<string>('');
  const [showInactive, setShowInactive] = useState(true);
  const [editing, setEditing] = useState<{ reply: QuickReply | null } | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const all = listQ.data ?? [];
  const list = filterQuickReplies(all, query).filter(
    (r) =>
      (showInactive || r.active) &&
      (deskFilter === ''
        ? true
        : deskFilter === 'global'
          ? r.desk === null
          : r.desk === null || String(r.desk.id) === deskFilter)
  );
  const inactiveCount = all.filter((r) => !r.active).length;

  return (
    <div className="rounded-lg border bg-card p-4 shadow-xs sm:p-5">
      <SettingsBreadcrumb section="Geral" page="Respostas rápidas" />
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <h1 className="text-xl leading-7 font-semibold">Respostas rápidas</h1>
        <div className="ml-auto flex items-center gap-2">
          <SettingsSearch value={query} onChange={setQuery} label="Buscar resposta rápida" />
          {isAdmin ? (
            <Button size="sm" className="h-9" onClick={() => setEditing({ reply: null })}>
              <Plus className="size-4" strokeWidth={2.5} />
              Resposta
            </Button>
          ) : null}
        </div>
      </div>
      <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
        Mensagens prontas para a conversa. No compositor, digite{' '}
        <code className="rounded-sm bg-muted px-1">/</code> e o atalho, ou use o botão “Respostas
        rápidas”: o texto entra na caixa com as variáveis preenchidas, para você revisar antes de
        enviar.
        {isAdmin ? null : ' Só administradores criam e editam.'}
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
        <label className="flex items-center gap-2">
          Mesa
          <Select
            aria-label="Filtrar por mesa"
            value={deskFilter}
            onChange={(e) => setDeskFilter(e.target.value)}
            className="h-8 w-56 py-0 text-sm"
          >
            <option value="">Todas</option>
            <option value="global">Só as de todas as mesas</option>
            {(desksQ.data ?? []).map((d) => (
              <option key={d.id} value={String(d.id)}>
                {d.displayName}
              </option>
            ))}
          </Select>
        </label>
        <label className="flex cursor-pointer items-center gap-2">
          <Checkbox
            checked={showInactive}
            onCheckedChange={(v) => setShowInactive(v === true)}
            aria-label="Mostrar inativas"
          />
          Mostrar inativas{inactiveCount > 0 ? ` (${inactiveCount})` : ''}
        </label>
        {listQ.data ? (
          <span>
            {list.length} resposta{list.length === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>

      {rowError ? (
        <p className="mt-2 text-sm text-destructive" role="alert">
          {rowError}
        </p>
      ) : null}

      <div className="mt-2">
        {listQ.isPending ? (
          <div className="space-y-2" aria-busy>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : listQ.isError ? (
          <EmptyState message={`Erro ao carregar: ${quickReplyErrorMessage(listQ.error)}`} />
        ) : list.length === 0 ? (
          <EmptyState
            className="h-52"
            message={
              all.length === 0
                ? 'Nenhuma resposta rápida cadastrada'
                : 'Nenhuma resposta encontrada com esses filtros'
            }
          />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-10 w-40 px-4 font-semibold">Atalho</th>
                  <th className="h-10 px-4 font-semibold">Resposta</th>
                  <th className="h-10 w-44 px-4 font-semibold">Mesa</th>
                  <th className="h-10 w-28 px-4 font-semibold">Status</th>
                  {isAdmin ? (
                    <th className="h-10 w-32 px-4">
                      <span className="sr-only">Ações</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-divider align-top transition-colors last:border-0 hover:bg-accent/60"
                  >
                    <td className="px-4 py-2.5">
                      <code className="rounded-sm bg-muted px-1.5 py-0.5 font-mono text-[13px]">
                        /{r.shortcut}
                      </code>
                    </td>
                    <td className="max-w-xl px-4 py-2.5">
                      <span
                        className={`block truncate font-medium ${r.active ? '' : 'text-muted-foreground'}`}
                      >
                        {r.title}
                      </span>
                      <span className="mt-0.5 line-clamp-2 text-xs whitespace-pre-line text-muted-foreground">
                        {r.body}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {r.desk?.name ?? 'Todas as mesas'}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant={r.active ? 'success' : 'default'}>
                        {r.active ? 'Ativa' : 'Inativa'}
                      </Badge>
                    </td>
                    {isAdmin ? (
                      <td className="px-4 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <Switch
                            aria-label={
                              r.active ? `Desativar /${r.shortcut}` : `Ativar /${r.shortcut}`
                            }
                            checked={r.active}
                            disabled={save.isPending}
                            onCheckedChange={(active) => {
                              setRowError(null);
                              save.mutate(
                                { id: r.id, body: { active } },
                                { onError: (e) => setRowError(quickReplyErrorMessage(e)) }
                              );
                            }}
                          />
                          <DeskRowAction
                            label={`Editar /${r.shortcut}`}
                            onClick={() => setEditing({ reply: r })}
                          >
                            <Pencil className="size-3.5" />
                          </DeskRowAction>
                          <DeskRowAction
                            label={`Excluir /${r.shortcut}`}
                            onClick={() => {
                              if (!window.confirm(`Excluir a resposta rápida "/${r.shortcut}"?`))
                                return;
                              setRowError(null);
                              remove.mutate(r.id, {
                                onError: (e) => setRowError(quickReplyErrorMessage(e)),
                              });
                            }}
                          >
                            <Trash2 className="size-3.5" />
                          </DeskRowAction>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing ? <QuickReplyDialog reply={editing.reply} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

/** Enquanto digita: sem acento e minúsculo, sem cortar o `-` do fim (o slug completo sai no blur). */
function typingShortcut(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^[-_]+/, '')
    .slice(0, QUICK_REPLY_SHORTCUT_MAX);
}

function QuickReplyDialog({ reply, onClose }: { reply: QuickReply | null; onClose: () => void }) {
  const user = useAuthUser();
  const variables = useVariables();
  const desksQ = useQuery(desksListQuery());
  const save = useSaveQuickReply();
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [title, setTitle] = useState(reply?.title ?? '');
  const [shortcut, setShortcut] = useState(reply?.shortcut ?? '');
  // o atalho segue o título até a pessoa mexer nele (resposta nova)
  const [shortcutTouched, setShortcutTouched] = useState(reply !== null);
  const [body, setBody] = useState(reply?.body ?? '');
  const [deskId, setDeskId] = useState<string>(reply?.desk ? String(reply.desk.id) : '');
  const [active, setActive] = useState(reply?.active ?? true);

  const desks = (desksQ.data ?? []).filter((d) => d.active || String(d.id) === deskId);
  const known = new Set(variables.map((v) => v.token));
  // a prévia: os exemplos, com o primeiro nome de quem está editando no {atendente}
  const samples = variables.map((v) => ({
    token: v.token,
    value: v.key === 'attendant' ? (firstNameOf(user?.name) ?? v.sample) : v.sample,
  }));
  const preview = fillQuickReply(body, samples);
  const unknown = unresolvedVariables(body).filter(
    (t) => !known.has(variableToken(t.slice(1, -1)))
  );

  const shortcutValid = QUICK_REPLY_SHORTCUT_RE.test(shortcut);
  const valid =
    title.trim() !== '' &&
    shortcutValid &&
    body.trim() !== '' &&
    body.length <= QUICK_REPLY_BODY_MAX;

  const insertVariable = (token: string) => {
    const el = bodyRef.current;
    const piece = `{${token}}`;
    const start = el?.selectionStart ?? body.length;
    const end = el?.selectionEnd ?? body.length;
    const next = body.slice(0, start) + piece + body.slice(end);
    setBody(next);
    window.requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + piece.length, start + piece.length);
    });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    const fields = {
      title: title.trim(),
      shortcut,
      body,
      deskId: deskId === '' ? null : Number(deskId),
      active,
    };
    save.mutate(reply ? { id: reply.id, body: fields } : { id: null, body: fields }, {
      onSuccess: onClose,
    });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={reply ? 'Editar resposta rápida' : 'Nova resposta rápida'}
      className="sm:max-w-3xl"
    >
      <form onSubmit={submit} className="grid gap-5 md:grid-cols-[minmax(0,1fr)_16rem]">
        <div className="min-w-0 space-y-4">
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_12rem]">
            <div className="space-y-1.5">
              <Label htmlFor="qr-title">
                Título <span className="text-destructive">*</span>
              </Label>
              <Input
                id="qr-title"
                value={title}
                maxLength={QUICK_REPLY_TITLE_MAX}
                autoFocus
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (!shortcutTouched) setShortcut(slugifyShortcut(e.target.value));
                }}
                placeholder="Saudação inicial"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="qr-shortcut">
                Atalho <span className="text-destructive">*</span>
              </Label>
              <div className="flex items-center rounded-md border border-input bg-card focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50">
                <span className="pl-2.5 font-mono text-sm text-muted-foreground">/</span>
                <input
                  id="qr-shortcut"
                  value={shortcut}
                  onChange={(e) => {
                    setShortcutTouched(true);
                    setShortcut(typingShortcut(e.target.value));
                  }}
                  onBlur={() => setShortcut((s) => slugifyShortcut(s))}
                  className="h-9 min-w-0 flex-1 bg-transparent pr-2.5 pl-0.5 font-mono text-sm outline-none"
                  placeholder="saudacao"
                  aria-invalid={shortcut !== '' && !shortcutValid}
                />
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
            <div className="space-y-1.5">
              <Label htmlFor="qr-desk">Mesa</Label>
              <Select id="qr-desk" value={deskId} onChange={(e) => setDeskId(e.target.value)}>
                <option value="">Todas as mesas</option>
                {desks.map((d) => (
                  <option key={d.id} value={String(d.id)}>
                    {d.displayName}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex h-9 items-center gap-2">
              <Switch
                id="qr-active"
                aria-label="Resposta ativa"
                checked={active}
                onCheckedChange={setActive}
              />
              <Label htmlFor="qr-active" className="cursor-pointer">
                Ativa
              </Label>
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-2">
              <Label htmlFor="qr-body">
                Texto <span className="text-destructive">*</span>
              </Label>
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {body.length}/{QUICK_REPLY_BODY_MAX}
              </span>
            </div>
            <Textarea
              id="qr-body"
              ref={bodyRef}
              rows={6}
              value={body}
              maxLength={QUICK_REPLY_BODY_MAX}
              onChange={(e) => setBody(e.target.value)}
              placeholder={'Olá, {primeiro_nome}! Aqui é {atendente}.'}
            />
            <p className="text-xs text-muted-foreground">
              Formatação do WhatsApp: *negrito*, _itálico_, ~riscado~, `mono`.
            </p>
          </div>

          <div className="space-y-1.5">
            <p className="text-sm font-medium">Prévia (com valores de exemplo)</p>
            <div className="rounded-lg bg-[#E7F6E4] px-3 py-2 text-sm break-words whitespace-pre-wrap text-foreground">
              {body.trim() === '' ? (
                <span className="text-muted-foreground">O texto aparece aqui.</span>
              ) : (
                <WhatsappText text={preview.text} />
              )}
            </div>
            {unknown.length > 0 ? (
              <p className="text-xs text-[#AD6800]">
                Variável desconhecida: {unknown.join(', ')} — vai aparecer assim para o atendente,
                que terá de completar à mão.
              </p>
            ) : null}
          </div>

          {save.error ? (
            <p className="text-sm text-destructive">{quickReplyErrorMessage(save.error)}</p>
          ) : null}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" size="sm" disabled={save.isPending || !valid}>
              {save.isPending ? 'Salvando…' : 'Salvar'}
            </Button>
          </div>
        </div>

        <aside
          className="space-y-2 rounded-md border bg-muted/30 p-3 text-sm"
          aria-label="Variáveis"
        >
          <p className="font-medium">Variáveis</p>
          <p className="text-xs text-muted-foreground">
            Clique para inserir no texto. Na conversa, cada uma é trocada pelo dado dela; o que a
            conversa não tiver fica escrito entre chaves, para o atendente ver antes de enviar.
          </p>
          <ul className="space-y-1.5">
            {variables.map((v) => (
              <li key={v.key}>
                <button
                  type="button"
                  onClick={() => insertVariable(v.token)}
                  className="w-full rounded-md border bg-card px-2 py-1.5 text-left hover:border-primary"
                >
                  <code className="font-mono text-xs text-primary">{`{${v.token}}`}</code>
                  <span className="block text-xs text-muted-foreground">
                    {v.label} · ex.:{' '}
                    {v.key === 'attendant' ? (firstNameOf(user?.name) ?? v.sample) : v.sample}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>
      </form>
    </Dialog>
  );
}
